import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import useStyles from 'isomorphic-style-loader/useStyles';
import type { CSSProperties, ReactNode } from 'react';
import { forwardRef } from 'react';

import s from './context-panel-outlet.scss';

export const DEFAULT_PANEL_WIDTH = '30rem';

const transition = {
  bounce: 0,
  duration: 0.5,
  type: 'spring',
} as const;

export interface ContextPanelOutletProps {
  className?: string;
  /**
   * The panel body content.
   */
  content?: ReactNode;
  /**
   * When true, the panel skips its slide-out animation on close. See
   * `ContextPanelOpenOptions.exitImmediate`.
   *
   * @default false
   */
  exitImmediate?: boolean;
  /**
   * Custom header content. Typically a `ContextPanelHeader` element, but any
   * ReactNode is accepted.
   */
  header?: ReactNode;
  /**
   * Whether the panel is open.
   */
  open?: boolean;
  /**
   * Whether to apply default padding to the body content.
   *
   * @default true
   */
  padded?: boolean;
  /**
   * Inline styles applied to the root element. Used by scope integrations for
   * dynamic positioning (e.g. computed `top` value for page scope).
   */
  style?: CSSProperties;
  /**
   * Panel width when open.
   *
   * @default '30rem'
   */
  width?: string;
}

/**
 * A presentational side panel component. Renders an animated `<aside>` with a
 * header and scrollable body. Receives state as props — integration points
 * (Page, Layout, ContextPanelRegion) are responsible for reading context and
 * passing props.
 */
export const ContextPanelOutlet = forwardRef<
  HTMLElement,
  ContextPanelOutletProps
>(
  (
    {
      className,
      content,
      exitImmediate = false,
      header,
      open: isOpen = false,
      padded = true,
      style,
      width = DEFAULT_PANEL_WIDTH,
    },
    forwardedRef,
  ) => {
    useStyles(s);

    const inner = (
      <div className={s.inner} style={{ minWidth: width }}>
        {header}

        <div className={classNames(s.body, padded && s.padded)}>{content}</div>
      </div>
    );

    /**
     * `exitImmediate` bypasses `AnimatePresence` so the panel unmounts
     * in the same frame as `isOpen` flipping — even with a duration-0
     * exit transition, `AnimatePresence` holds the exiting child for
     * one extra frame.
     */
    if (exitImmediate) {
      return isOpen ? (
        <motion.aside
          ref={forwardedRef}
          className={classNames(s.root, className)}
          style={style}
          animate={{ width }}
          initial={{ width: 0 }}
          transition={transition}
        >
          {inner}
        </motion.aside>
      ) : null;
    }

    return (
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.aside
            ref={forwardedRef}
            className={classNames(s.root, className)}
            style={style}
            animate={{ width }}
            exit={{ width: 0 }}
            initial={{ width: 0 }}
            transition={transition}
          >
            {inner}
          </motion.aside>
        )}
      </AnimatePresence>
    );
  },
);
ContextPanelOutlet.displayName = 'ContextPanel.Outlet';

export default ContextPanelOutlet;
